import type { QueryClient } from '@tanstack/react-query';

import { onResume } from '../lifecycle/resume.ts';
import { flushQueue } from './flushQueue.ts';
import { onQueueChanged, pending, type QueueEntry } from './mutationQueue.ts';

export interface QueueSnapshot {
  isOnline: boolean;
  /** Mutations applied locally but not yet confirmed by the server. */
  queued: number;
  isFlushing: boolean;
  /**
   * The intents themselves, oldest first.
   *
   * The count alone was enough while the only consumer was an indicator saying how many
   * changes were unsent. It is not enough to *draw* the list: after a reload the query
   * cache is gone and the service worker answers with the snapshot from before those
   * changes, so the rows have to be replayed over it (`applyPending`). Publishing them
   * here rather than reading IndexedDB per component keeps one copy, updated at the same
   * moments the count already was.
   */
  entries: QueueEntry[];
}

type Listener = () => void;

/**
 * Queue state as a module-level store, read through `useSyncExternalStore`.
 *
 * The queue genuinely *is* an external system — it lives in IndexedDB and is driven by
 * browser connectivity events, not by React. Mirroring it into component state via
 * effects means cascading renders and a copy that can disagree with the database; a
 * store with a subscription is the shape React actually wants for this, and it lets any
 * component read the same truth without each running its own flush loop.
 */
let snapshot: QueueSnapshot = {
  isOnline: typeof navigator === 'undefined' ? true : navigator.onLine,
  queued: 0,
  isFlushing: false,
  entries: [],
};

const listeners = new Set<Listener>();

// The flush in progress, if any. Held as the promise rather than a boolean so that a
// second caller *joins* it instead of being turned away: the reconcile on resume has to
// know the queue has actually drained before it re-reads the list, or it would briefly
// wipe unsent taps off the screen and then put them back. It also still guards against
// two flushes running at once, which would send the queue out of order.
let inFlight: Promise<void> | null = null;
let queryClient: QueryClient | null = null;
let started = false;
let unsubscribeFromQueue: (() => void) | null = null;

function emit(next: Partial<QueueSnapshot>): void {
  const updated = { ...snapshot, ...next };

  // useSyncExternalStore compares by identity, so only publish real changes — otherwise
  // every no-op refresh re-renders the list.
  if (
    updated.isOnline === snapshot.isOnline &&
    updated.isFlushing === snapshot.isFlushing &&
    sameEntries(updated.entries, snapshot.entries)
  ) {
    return;
  }

  snapshot = updated;

  for (const listener of listeners) {
    listener();
  }
}

/**
 * Whether two reads of the queue are the same queue.
 *
 * Comparing ids is enough: they are IndexedDB's auto-increment key, and a stored mutation
 * is never edited — only its attempt count is, which changes nothing anyone draws. Same
 * ids in the same order therefore means the queue has not moved, however many times it has
 * been read. Deep-comparing the intents would also have to deal with a Blob.
 */
function sameEntries(a: QueueEntry[], b: QueueEntry[]): boolean {
  return a.length === b.length && a.every((entry, index) => entry.id === b[index]?.id);
}

export function subscribeToQueue(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getQueueSnapshot(): QueueSnapshot {
  return snapshot;
}

/**
 * Re-reads the queue. Call after enqueuing so the indicator reacts at once — and so the
 * list draws the change, which after a reload is the only thing that will.
 */
export async function refreshQueue(): Promise<void> {
  const entries = await pending();

  emit({ entries, queued: entries.length });
}

/**
 * Sends whatever is queued, and resolves when it is done — including when another caller
 * started the flush that is doing the sending.
 */
export function flush(): Promise<void> {
  inFlight ??= run().finally(() => {
    inFlight = null;
  });

  return inFlight;
}

async function run(): Promise<void> {
  if (!queryClient) {
    return;
  }

  const outstanding = await pending();
  if (outstanding.length === 0) {
    emit({ entries: [], queued: 0 });
    return;
  }

  emit({ isFlushing: true, entries: outstanding, queued: outstanding.length });

  try {
    await flushQueue(queryClient);
  } finally {
    // One emit, not two: the rows drawn from the queue and the ones drawn from the
    // refetched list must swap over in a single render, or a flushed item blinks out and
    // back. flushQueue only resolves once it has reconciled, so by here the server's
    // answer is already in the cache.
    const remaining = await pending();

    emit({ isFlushing: false, entries: remaining, queued: remaining.length });
  }
}

/**
 * Starts listening for the connection coming back. Idempotent, so mounting several
 * components that use the queue does not attach duplicate listeners or racing flushes.
 *
 * `navigator.onLine` is only a hint — it reports the network interface, not whether
 * anything is reachable, and a supermarket's captive wifi will happily claim to be
 * online. It triggers a flush attempt; the request itself is the real test.
 */
export function startQueue(client: QueryClient): void {
  queryClient = client;

  if (started) {
    void refreshQueue();
    return;
  }

  started = true;

  // Anything that queues a mutation publishes it here, which is the only way the screen
  // learns about a change that could not be sent. Nothing else knows the moment it lands:
  // a mutation is queued from its own onError, long after the tap that caused it.
  unsubscribeFromQueue = onQueueChanged(() => void refreshQueue());

  window.addEventListener('online', () => {
    emit({ isOnline: true });
    void flush();
  });

  window.addEventListener('offline', () => emit({ isOnline: false }));

  // Returning to the app is a more reliable signal than the 'online' event: a phone that
  // slept through the reconnection never fires one. This used to be a visibilitychange
  // listener of its own; it is now one subscriber among several to the same event, so
  // that the flush and the reconcile that follows it cannot be ordered differently on
  // different resumes. See lifecycle/resume.ts.
  onResume(() => void flush());

  void flush();
}

/** Test seam: resets module state between test cases. */
export function resetQueueStoreForTests(): void {
  snapshot = { isOnline: true, queued: 0, isFlushing: false, entries: [] };
  listeners.clear();
  unsubscribeFromQueue?.();
  unsubscribeFromQueue = null;
  inFlight = null;
  queryClient = null;
  started = false;
}
